import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Card from '../components/Card';
import { useLanguage } from '../context/LanguageContext';
import { useUI } from '../context/UIContext';
import {
    Heart, Activity, Car, Home as HomeIcon, Plane, Briefcase,
    PlusCircle, Search, FileCheck, Shield, Headphones,
    ClipboardCheck, Zap, Star, ArrowRight, Award
} from 'lucide-react';
import './Home.css';
import './Insurance.css';

const Home = () => {
    const { t } = useLanguage();
    const { openPopup } = useUI();
    const [activeStep, setActiveStep] = useState(0);

    const homeProducts = [
        { title: 'Life Insurance', description: 'Secure your family\'s future with our comprehensive life insurance plans.', icon: Heart, link: '/insurance/life-insurance' },
        { title: 'Health Insurance', description: 'Quality healthcare coverage for you and your loved ones.', icon: Activity, link: '/insurance/health-insurance' },
        { title: 'Motor Insurance', description: 'Fast claim settlements and wide garage network for your vehicle.', icon: Car, link: '/insurance/motor-insurance' },
        { title: 'Property Insurance', description: 'Protect your home and assets with our property coverage.', icon: HomeIcon, link: '/insurance/property-insurance' },
        { title: 'Travel Insurance', description: 'Travel the world with peace of mind and 24/7 assistance.', icon: Plane, link: '/insurance/travel-insurance' },
        { title: 'Business Insurance', description: 'Safeguard your business against unforeseen risks.', icon: Briefcase, link: '/insurance/business-insurance' },
        { title: 'Others', description: 'Specialized insurance for your pets, gadgets, and more.', icon: PlusCircle, link: '/insurance/mobile-insurance' },
    ];

    const steps = [
        { icon: Search, label: t('hiw_step1'), desc: t('hiw_step1_desc') },
        { icon: ClipboardCheck, label: t('hiw_step2'), desc: t('hiw_step2_desc') },
        { icon: FileCheck, label: t('hiw_step3'), desc: t('hiw_step3_desc') },
        { icon: Shield, label: t('hiw_step4'), desc: t('hiw_step4_desc') },
    ];

    const benefits = [
        { icon: Zap, title: t('benefit_1'), desc: t('benefit_1_desc') },
        { icon: Headphones, title: t('benefit_2'), desc: t('benefit_2_desc') },
        { icon: FileCheck, title: t('benefit_3'), desc: t('benefit_3_desc') },
    ];

    const testimonials = [
        { text: t('testimonial_1'), name: t('testimonial_1_name'), role: t('testimonial_1_role'), image: '/assets/user1.png', stars: 5 },
        { text: t('testimonial_2'), name: t('testimonial_2_name'), role: t('testimonial_2_role'), image: '/assets/user2.png', stars: 5 },
        { text: t('testimonial_3'), name: t('testimonial_3_name'), role: t('testimonial_3_role'), image: '/assets/user3.png', stars: 5 },
    ];

    const insuranceGroups = [
        {
            heading: 'Life Insurance',
            items: [
                { name: 'Term Insurance', image: '/assets/term.jpg' },
                { name: 'Whole Life Insurance', image: '/assets/whole-life.jpg' },
                { name: 'Endowment Plan', image: '/assets/endowment.jpg' },
                { name: 'Money Back Plan', image: '/assets/money-back.jpg' },
                { name: 'ULIP (Unit Linked Plan)', image: '/assets/ulip.jpg' },
                { name: 'Pension Plan', image: '/assets/pension.jpg' }
            ]
        },
        {
            heading: 'Health Insurance',
            items: [
                { name: 'Individual Health Insurance', image: '/assets/individual-health.jpg' },
                { name: 'Family Floater Insurance', image: '/assets/family-floater.jpg' },
                { name: 'Senior Citizen Health Insurance', image: '/assets/senior-health.jpg' },
                { name: 'Critical Illness Insurance', image: '/assets/critical-illness.jpg' }
            ]
        },
        {
            heading: 'Motor Insurance',
            items: [
                { name: 'Car Insurance', image: '/assets/car-insurance.jpg' },
                { name: 'Two-Wheeler Insurance', image: '/assets/bike-insurance.jpg' },
                { name: 'Commercial Vehicle Insurance', image: '/assets/commercial-vehicle.jpg' },
                { name: 'Third-Party Insurance', image: '/assets/third-party.jpg' },
                { name: 'Comprehensive Motor Insurance', image: '/assets/comprehensive-motor.jpg' }
            ]
        },
        {
            heading: 'Property Insurance',
            items: [
                { name: 'Home Insurance', image: '/assets/home-insurance.jpg' },
                { name: 'Fire Insurance', image: '/assets/fire-insurance.jpg' },
                { name: 'Burglary Insurance', image: '/assets/burglary.jpg' },
                { name: 'Shop Insurance', image: '/assets/shop-insurance.jpg' },
                { name: 'Industrial Insurance', image: '/assets/industrial-insurance.jpg' }
            ]
        },
        {
            heading: 'Business Insurance',
            items: [
                { name: 'Liability Insurance', image: '/assets/liability.jpg' },
                { name: 'Professional Indemnity', image: '/assets/professional-indemnity.jpg' },
                { name: 'Cyber Insurance', image: '/assets/cyber-insurance.jpg' },
                { name: 'Marine Insurance', image: '/assets/marine-insurance.jpg' },
                { name: 'Workmen Compensation', image: '/assets/workmen.jpg' }
            ]
        },
        {
            heading: 'Travel Insurance',
            items: [
                { name: 'Domestic Travel Insurance', image: '/assets/domestic-travel.jpg' },
                { name: 'International Travel Insurance', image: '/assets/inter-travel.jpg' },
                { name: 'Student Travel Insurance', image: '/assets/student-travel.jpg' },
                { name: 'Family Travel Insurance', image: '/assets/family-travel.jpg' }
            ]
        },
        {
            heading: 'Other Insurance',
            items: [
                { name: 'Mobile Insurance', image: '/assets/mobile-insurance.jpg' },
                { name: 'Pet Insurance', image: '/assets/pet-insurance.jpg' },
                { name: 'Event Insurance', image: '/assets/event-insurance.jpg' },
                { name: 'Wedding Insurance', image: '/assets/wedding-insurance.jpg' }
            ]
        },
    ];

    return (
        <div className="home-page">
            <Hero
                title={t('hero_title')}
                subtitle={t('hero_subtitle')}
                bgGradient={true}
                image="/assets/home-hero.png"
                imageAlt="Insurance coverage illustration"
                trustSignal={t('hero_trust')}
                primaryCta={{ label: 'Get Your Free Quote', onClick: openPopup }}
                secondaryCta={{ label: 'Contact Experts', onClick: () => window.location.href = '/contact' }}
            />

            {/* ── Insurance Categories — Individual Cards ── */}
            <section className="insurance-categories-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-eyebrow">OUR PRODUCTS</span>
                        <h2 className="section-title">Explore Insurance Plans</h2>
                        <p className="section-subtitle">Find the right coverage tailored to your needs — all in one place.</p>
                    </div>

                    {insuranceGroups.map((group, gi) => (
                        <div className="insurance-group" key={gi}>
                            <h3 className="insurance-group-heading">{group.heading}</h3>
                            <div className="insurance-items-grid">
                                {group.items.map((item, ii) => (
                                    <div className="insurance-item-card" key={ii} onClick={() => openPopup(item.name)}>
                                        <div className="insurance-item-img-host">
                                            <img src={item.image} alt={item.name} className="insurance-item-img" />
                                        </div>
                                        <div className="insurance-item-content">
                                            <span className="insurance-item-name">{item.name}</span>
                                            <ArrowRight size={14} className="insurance-item-arrow" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Stats Strip */}
            <section className="stats-strip">
                <div className="container">
                    <div className="stats-grid">
                        <div className="stat-item">
                            <div className="stat-number">{t('stat_1_num')}</div>
                            <div className="stat-label">{t('stat_1_label')}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">{t('stat_2_num')}</div>
                            <div className="stat-label">{t('stat_2_label')}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">{t('stat_3_num')}</div>
                            <div className="stat-label">{t('stat_3_label')}</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">{t('stat_4_num')}</div>
                            <div className="stat-label">{t('stat_4_label')}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Suite — Minimalist Serious Grid */}
            <section className="section-padding products-suite">
                <div className="container">
                    <div className="section-header">
                        <span className="section-eyebrow">ELITE PROTECTION</span>
                        <h2 className="section-title">Insurance for Every Need</h2>
                        <p className="section-subtitle">Choose from our wide range of tailored plans to protect what matters most.</p>
                    </div>

                    <div className="services-suite-grid">
                        {homeProducts.map((p, i) => {
                            const IconComp = p.icon;
                            return (
                                <Link to={p.link} className="service-vibe-card" key={i}>
                                    <div className="service-vibe-icon">
                                        <IconComp size={28} />
                                    </div>
                                    <div className="service-vibe-content">
                                        <h3>{p.title}</h3>
                                        <p>{p.description}</p>
                                        <span className="service-vibe-link">Learn More <ArrowRight size={16} /></span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* How It Works — Interactive Stepper */}
            <section className="section-padding how-it-works">
                <div className="container">
                    <div className="section-header">
                        <span className="section-eyebrow">SMART PROCESS</span>
                        <h2 className="section-title">How It Works</h2>
                        <p className="section-subtitle">Get covered in four simple steps — no paperwork, no hassle.</p>
                    </div>

                    <div className="hiw-interactive-container">
                        <div className="hiw-steps-nav">
                            {steps.map((step, i) => {
                                const StepIcon = step.icon;
                                const isActive = activeStep === i;
                                return (
                                    <div
                                        key={i}
                                        className={`hiw-step-trigger ${isActive ? 'active' : ''}`}
                                        onClick={() => setActiveStep(i)}
                                        onMouseEnter={() => setActiveStep(i)}
                                    >
                                        <div className="hiw-step-icon-host">
                                            <StepIcon size={24} />
                                            <span className="hiw-step-tag">{i + 1}</span>
                                        </div>
                                        <div className="hiw-step-label">
                                            <h4>{step.label}</h4>
                                            <div className="hiw-progress-line">
                                                <div className="hiw-progress-fill" style={{ width: isActive ? '100%' : '0%' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="hiw-visual-terminal glass">
                            <div className="terminal-status-ring">
                                <div className={`terminal-icon-center ${activeStep === 0 ? 'pulse' : ''}`}>
                                    {React.createElement(steps[activeStep].icon, { size: 64 })}
                                </div>
                            </div>
                            <div className="terminal-content">
                                <div className="terminal-badge">STEP 0{activeStep + 1} ACTIVE</div>
                                <h3>{steps[activeStep].label}</h3>
                                <p>{steps[activeStep].desc}</p>
                                <div className="terminal-action-row">
                                    <button className="btn btn-primary btn-sm" onClick={openPopup}>Get Started Now</button>
                                </div>
                            </div>

                            {/* Decorative terminal elements */}
                            <div className="terminal-dot top-left"></div>
                            <div className="terminal-dot top-right"></div>
                            <div className="terminal-glitch-line"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Bharatinsurex — Elite Benefits Suite */}
            <section className="section-padding benefits-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-eyebrow">ELITE TRUST</span>
                        <h2 className="section-title">Why Bharatinsurex</h2>
                        <p className="section-subtitle">Built on industry-leading trust and transparency. We go beyond policy documents to truly protect your peace of mind.</p>
                    </div>

                    <div className="benefits-image-grid">
                        <img src="/assets/claim.png" alt="99.7% Claim Settlement" className="elite-visual-direct" />
                        <img src="/assets/support.png" alt="24/7/365 Expert Support" className="elite-visual-direct" />
                        <img src="/assets/paperless.png" alt="100% Paperless Digital" className="elite-visual-direct" />
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="section-padding testimonials-section">
                <div className="container">
                    <div className="section-header">
                        <span className="section-eyebrow">{t('testimonials_eyebrow')}</span>
                        <h2 className="section-title">{t('testimonials_title')}</h2>
                        <p className="section-subtitle">{t('testimonials_subtitle')}</p>
                    </div>
                    <div className="testimonials-grid">
                        {testimonials.map((tm, i) => (
                            <div className="testimonial-card" key={i}>
                                <div className="testimonial-stars">
                                    {Array.from({ length: tm.stars }).map((_, j) => (
                                        <Star key={j} size={16} className="star" fill="#facc15" />
                                    ))}
                                </div>
                                <p className="testimonial-text">"{tm.text}"</p>
                                <div className="testimonial-author">
                                    <div className="author-avatar-host">
                                        <img src={tm.image} alt={tm.name} className="author-avatar-img" />
                                    </div>
                                    <div className="author-info">
                                        <h4>{tm.name}</h4>
                                        <p>{tm.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Full-Horizon Conversion Terminal — Full Width Section */}
            <section className="cta-full-horizon">
                <div className="cta-terminal-host-full">
                    <div className="container">
                        <div className="cta-terminal-content">
                            <span className="terminal-badge-small">TRUSTED BY 2 LAKH+ INDIANS</span>
                            <h2 className="terminal-title">Ready to Protect What Matters?</h2>
                            <p className="terminal-desc">Join 2 lakh+ customers who chose smarter insurance. Get a personalised quote in under 2 minutes.</p>
                            <div className="cta-buttons-terminal">
                                <button className="btn-vibe-primary" onClick={openPopup}>Get Started Now</button>
                            </div>
                        </div>
                    </div>

                    {/* Decorative Elite Visuals */}
                    <div className="cta-visual-orb orb-1"></div>
                    <div className="cta-visual-orb orb-2"></div>
                    <div className="cta-visual-orb orb-3"></div>
                    <div className="cta-visual-mesh"></div>
                </div>
            </section>
        </div>
    );
};

export default Home;
